import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { ConfigProvider } from 'antd';
import ptBR from 'antd/locale/pt_BR';
import { auth, ensureUserProfile } from '@festpix/services';
import { globalStyles, antdTheme } from '@festpix/ui';
import type { UserProfile } from '@festpix/types';

// Attendee Screens
import AttendeeLoginScreen from './screens/attendee/LoginScreen';
import AttendeeHomeScreen from './screens/attendee/HomeScreen';
import QRScreen from './screens/attendee/QRScreen';
import TransactionsScreen from './screens/attendee/TransactionsScreen';
import RechargeScreen from './screens/attendee/RechargeScreen';
import CreateChildWalletScreen from './screens/attendee/CreateChildWalletScreen';
import TransferToChildScreen from './screens/attendee/TransferToChildScreen';

// Admin Screens
import DashboardPage from './screens/admin/DashboardPage';
import WalletsPage from './screens/admin/WalletsPage';
import CreateWalletPage from './screens/admin/CreateWalletPage';
import BoothWalletsPage from './screens/admin/BoothWalletsPage';
import UsersPage from './screens/admin/UsersPage';
import AdminTransactionsPage from './screens/admin/TransactionsPage';
import BoothsPage from './screens/admin/BoothsPage';
import BoothSKUsPage from './screens/admin/BoothSKUsPage';
import MenuViewPage from './screens/admin/MenuViewPage';
import ReportsPage from './screens/admin/ReportsPage';
import DangerZonePage from './screens/admin/DangerZonePage';
import AccessDeniedPage from './screens/admin/AccessDeniedPage';

// Vendor Screens
import VendorHomeScreen from './screens/vendor/HomeScreen';
import VendorScanScreen from './screens/vendor/ScanScreen';
import VendorChargeScreen from './screens/vendor/ChargeScreen';
import VendorTransactionsScreen from './screens/vendor/TransactionsScreen';

// Inject global styles
const styleElement = document.createElement('style');
styleElement.textContent = globalStyles;
document.head.appendChild(styleElement);

function LoadingSpinner() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FAFBFC'
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '3px solid #E0E0E0',
        borderTop: '3px solid #7A9B62',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
    </div>
  );
}

// =====================
// ATTENDEE ROUTES
// =====================

function AttendeePrivateRoute({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AttendeePublicRoute({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AttendeeApp() {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const profile = await ensureUserProfile(currentUser.uid, currentUser.email || '');
          setUserProfile(profile);
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setUserProfile(null);
      }

      setInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  if (initializing) {
    return <LoadingSpinner />;
  }

  // Redirect admins to admin area
  if (userProfile && userProfile.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  // Redirect vendors to vendor area
  if (userProfile && userProfile.role === 'vendor') {
    return <Navigate to="/vendor" replace />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AttendeePublicRoute>
            <AttendeeLoginScreen />
          </AttendeePublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <AttendeePrivateRoute>
            <AttendeeHomeScreen />
          </AttendeePrivateRoute>
        }
      />
      <Route
        path="/qr"
        element={
          <AttendeePrivateRoute>
            <QRScreen />
          </AttendeePrivateRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <AttendeePrivateRoute>
            <TransactionsScreen />
          </AttendeePrivateRoute>
        }
      />
      <Route
        path="/recharge"
        element={
          <AttendeePrivateRoute>
            <RechargeScreen />
          </AttendeePrivateRoute>
        }
      />
      <Route
        path="/create-child-wallet"
        element={
          <AttendeePrivateRoute>
            <CreateChildWalletScreen />
          </AttendeePrivateRoute>
        }
      />
      <Route
        path="/transfer"
        element={
          <AttendeePrivateRoute>
            <TransferToChildScreen />
          </AttendeePrivateRoute>
        }
      />
    </Routes>
  );
}

// =====================
// ADMIN ROUTES
// =====================

function AdminPrivateRoute({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const profile = await ensureUserProfile(currentUser.uid, currentUser.email || '');
          setUserProfile(profile);
        } catch (error) {
          console.error('Error loading user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has admin role
  if (!userProfile || userProfile.role !== 'admin') {
    return <Navigate to="/admin/access-denied" replace />;
  }

  return <>{children}</>;
}

function AdminApp() {
  return (
    <ConfigProvider theme={antdTheme} locale={ptBR}>
      <Routes>
        <Route
          path="access-denied"
          element={<AccessDeniedPage />}
        />
        <Route
          path=""
          element={
            <AdminPrivateRoute>
              <DashboardPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="wallets"
          element={
            <AdminPrivateRoute>
              <WalletsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="create-wallet"
          element={
            <AdminPrivateRoute>
              <CreateWalletPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="booth-wallets"
          element={
            <AdminPrivateRoute>
              <BoothWalletsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="users"
          element={
            <AdminPrivateRoute>
              <UsersPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="transactions"
          element={
            <AdminPrivateRoute>
              <AdminTransactionsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="booths"
          element={
            <AdminPrivateRoute>
              <BoothsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="booth-skus"
          element={
            <AdminPrivateRoute>
              <BoothSKUsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="booth-skus/:boothId"
          element={
            <AdminPrivateRoute>
              <BoothSKUsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="booths/:boothId/skus"
          element={
            <AdminPrivateRoute>
              <BoothSKUsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="menu"
          element={
            <AdminPrivateRoute>
              <MenuViewPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="menu/:boothId"
          element={
            <MenuViewPage />
          }
        />
        <Route
          path="reports"
          element={
            <AdminPrivateRoute>
              <ReportsPage />
            </AdminPrivateRoute>
          }
        />
        <Route
          path="danger-zone"
          element={
            <AdminPrivateRoute>
              <DangerZonePage />
            </AdminPrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </ConfigProvider>
  );
}

// =====================
// VENDOR ROUTES
// =====================

function VendorPrivateRoute({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function VendorApp() {
  return (
    <ConfigProvider theme={antdTheme} locale={ptBR}>
      <Routes>
        <Route
          path=""
          element={
            <VendorPrivateRoute>
              <VendorHomeScreen />
            </VendorPrivateRoute>
          }
        />
        <Route
          path="scan"
          element={
            <VendorPrivateRoute>
              <VendorScanScreen />
            </VendorPrivateRoute>
          }
        />
        <Route
          path="charge"
          element={
            <VendorPrivateRoute>
              <VendorChargeScreen />
            </VendorPrivateRoute>
          }
        />
        <Route
          path="transactions"
          element={
            <VendorPrivateRoute>
              <VendorTransactionsScreen />
            </VendorPrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="/vendor" replace />} />
      </Routes>
    </ConfigProvider>
  );
}

// =====================
// MAIN APP
// =====================

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Admin routes */}
        <Route path="/admin/*" element={<AdminApp />} />

        {/* Vendor routes */}
        <Route path="/vendor/*" element={<VendorApp />} />

        {/* Attendee routes (default) */}
        <Route path="/*" element={<AttendeeApp />} />
      </Routes>
    </BrowserRouter>
  );
}
